// PloyButton — primary button family for the employee portal redesign.
// Mirrors the `.btn` classes in /Users/harry/Downloads/employee-portal/project/styles.css
// (lines 96–156). Wraps Chakra's `Button` and applies exact theme tokens for
// each variant × size combination.

import { Button, ButtonProps as ChakraButtonProps } from "@chakra-ui/react";
import { forwardRef, ReactElement } from "react";

export type PloyButtonVariant =
  | "primary"
  | "secondary"
  | "outline-secondary"
  | "ghost"
  | "success"
  | "danger"
  | "danger-outline"
  | "azurite-link";

export type PloyButtonSize = "md" | "sm" | "icon";

export interface PloyButtonProps extends Omit<
  ChakraButtonProps,
  "variant" | "size" | "leftIcon" | "rightIcon"
> {
  variant?: PloyButtonVariant;
  size?: PloyButtonSize;
  leftIcon?: ReactElement;
  rightIcon?: ReactElement;
}

// Per-variant style maps (background, color, border, shadow, hover).
// Tokens map 1:1 with theme.ts. The only inlined values are the tinted
// border rgba()s from the design CSS — they don't have a theme equivalent.
const variantStyles: Record<PloyButtonVariant, Record<string, unknown>> = {
  primary: {
    bg: "p.500",
    color: "white",
    borderColor: "rgba(15, 60, 140, 0.25)",
    boxShadow: "buttonPrimary",
    fontWeight: 600, // matches the rendered design (TopNav inlines 600 over .btn's 500)
    _hover: { bg: "p.600", _disabled: { bg: "neutral.100" } },
  },
  secondary: {
    bg: "neutral.25",
    color: "neutral.900",
    borderColor: "neutral.300",
    _hover: {
      bg: "neutral.100",
      borderColor: "neutral.400",
      boxShadow: "elevLow",
      _disabled: { bg: "neutral.100" },
    },
  },
  "outline-secondary": {
    bg: "neutral.0",
    color: "neutral.900",
    borderColor: "transparent",
    boxShadow:
      "0 0.5px 1px 0 rgba(0, 0, 0, 0.25) inset, 0 1px 1px 0 rgba(0, 0, 0, 0.08), 0 0 0.5px 0 rgba(0, 0, 0, 0.25)",
    _hover: {
      bg: "neutral.0",
      backgroundImage:
        "linear-gradient(to top, var(--chakra-colors-neutral-0) 0%, var(--chakra-colors-neutral-100) 100%)",
      boxShadow:
        "0 0.5px 1px 0 rgba(0, 0, 0, 0.25) inset, 0 1px 1px 0 rgba(0, 0, 0, 0.08), 0 0 0.5px 0 rgba(0, 0, 0, 0.25)",
      _disabled: {
        bg: "neutral.100",
        backgroundImage: "none",
        boxShadow: "none",
      },
    },
  },
  ghost: {
    bg: "transparent",
    color: "neutral.800",
    borderColor: "transparent",
    _hover: { bg: "neutral.100", _disabled: { bg: "neutral.100" } },
  },
  success: {
    bg: "success.500",
    color: "white",
    borderColor: "rgba(20, 90, 50, 0.25)",
    boxShadow: "buttonSuccess",
    _hover: { bg: "success.600", _disabled: { bg: "neutral.100" } },
  },
  danger: {
    bg: "error.500",
    color: "white",
    borderColor: "rgba(140, 25, 25, 0.25)",
    boxShadow: "buttonDanger",
    _hover: { bg: "error.600", _disabled: { bg: "neutral.100" } },
  },
  "danger-outline": {
    bg: "transparent",
    color: "error.700",
    borderColor: "error.300",
    _hover: { bg: "error.50", _disabled: { bg: "neutral.100" } },
  },
  "azurite-link": {
    bg: "transparent",
    color: "p.600",
    borderColor: "transparent",
    fontWeight: 600,
    p: 0,
    h: "auto",
    minW: 0,
    boxShadow: "none",
    _hover: { color: "p.700", bg: "transparent" },
  },
};

// Size maps (padding, font-size). `.btn--icon` is a fixed 32x32 square.
const sizeStyles: Record<PloyButtonSize, Record<string, unknown>> = {
  md: {
    px: "14px",
    py: "8px",
    fontSize: "md", // 14px
    h: "auto",
    minH: "32px",
  },
  sm: {
    px: "10px",
    py: "6px",
    fontSize: "smd", // 13px
    h: "auto",
    minH: "28px",
  },
  icon: {
    w: "32px",
    h: "32px",
    minW: "32px",
    p: 0,
    fontSize: "md",
  },
};

const PloyButton = forwardRef<HTMLButtonElement, PloyButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      leftIcon,
      rightIcon,
      children,
      ...rest
    },
    ref,
  ) => {
    const v = variantStyles[variant];
    const s = variant === "azurite-link" ? {} : sizeStyles[size];

    // The azurite-link variant is a text link; scale on press would look
    // wrong on it (no surface to "press"). Every padded variant gets the
    // 0.96 scale on :active for tactile feedback (Emil rule).
    const wantsPressFeedback = variant !== "azurite-link";

    return (
      <Button
        ref={ref}
        // `.btn` base styles — lines 96–107 of styles.css.
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        gap="1xs" // 6px — matches CSS `gap: 6px`
        borderRadius="component.lg" // 8px
        borderWidth="1px"
        borderStyle="solid"
        borderColor="transparent"
        fontFamily="body"
        fontWeight={500}
        fontSize="md"
        lineHeight="1"
        whiteSpace="nowrap"
        cursor="pointer"
        // Strong ease-out curve from the design-eng playbook — built-in CSS
        // easings are too weak to feel intentional. Specific property list
        // (never `transition: all`).
        transition="background-color 120ms cubic-bezier(0.23, 1, 0.32, 1), border-color 120ms cubic-bezier(0.23, 1, 0.32, 1), color 120ms cubic-bezier(0.23, 1, 0.32, 1), box-shadow 120ms cubic-bezier(0.23, 1, 0.32, 1), transform 100ms cubic-bezier(0.23, 1, 0.32, 1)"
        {...(wantsPressFeedback && {
          _active: { transform: "scale(0.96)" },
        })}
        // Disabled — line 156 of styles.css.
        _disabled={{
          bg: "neutral.100",
          color: "neutral.400",
          cursor: "not-allowed",
          borderColor: "transparent",
          boxShadow: "none",
          opacity: 1,
          transform: "none",
        }}
        // Size, then variant — variant wins where they overlap (e.g. azurite-link's
        // `p: 0` overrides the size's padding).
        {...s}
        {...v}
        {...rest}
      >
        {leftIcon}
        {children}
        {rightIcon}
      </Button>
    );
  },
);

PloyButton.displayName = "PloyButton";

export default PloyButton;
